// ===================================================
// components/Layout/NavFlyout.jsx
// Панель подразделов хаба + режим редактирования состава.
// Stars ↔ user_menu_favorites SSOT (sync with top bar / menu-guide).
// ===================================================
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings as SettingOutlined, X as CloseOutlined, Plus as PlusOutlined, Star } from 'lucide-react';
import NavBadge from './NavBadge';
import NavFavoriteStar from './NavFavoriteStar';
import api from '../../services/index';
import { emitMenuFavoritesChanged } from '../../utils/menuFavoritesSync';

/** Snapshot of quickPicks for saves: keep array if present; never invent registry defaults. */
function pickQuickPicksForSave(hubOverride) {
  if (Array.isArray(hubOverride?.quickPicks)) {
    return [...hubOverride.quickPicks];
  }
  return undefined;
}

const NavFlyout = ({
  open,
  hub,
  baseHub = null,
  allBaseHubs = [],
  selectedKey,
  icons = {},
  favoriteKeys = [],
  onClose,
  onNavigate,
  hubOverride,
  onSaveHubOverride,
  onResetHub,
}) => {
  const [editMode, setEditMode] = useState(false);
  const Icon = icons[hub?.iconKey] || icons.appstore;

  useEffect(() => {
    if (!open) setEditMode(false);
  }, [open, hub?.id]);

  const favoriteSet = useMemo(
    () => new Set(Array.isArray(favoriteKeys) ? favoriteKeys : []),
    [favoriteKeys]
  );

  const hiddenKeys = useMemo(
    () => new Set(hubOverride?.hiddenItemKeys || []),
    [hubOverride]
  );

  const visibleItems = useMemo(() => {
    if (!hub) return [];
    let items = [...(hub.items || [])];
    if (Array.isArray(hubOverride?.itemOrder) && hubOverride.itemOrder.length) {
      const orderMap = new Map(hubOverride.itemOrder.map((k, i) => [k, i]));
      items = [...items].sort((a, b) => {
        const ai = orderMap.has(a.key) ? orderMap.get(a.key) : 1000;
        const bi = orderMap.has(b.key) ? orderMap.get(b.key) : 1000;
        return ai - bi;
      });
    }
    return items;
  }, [hub, hubOverride]);

  const visibleKeySet = useMemo(
    () => new Set(visibleItems.map((it) => it.key)),
    [visibleItems]
  );

  const addCandidates = useMemo(() => {
    const sourceHub = baseHub || hub;
    if (!sourceHub) return { defaultsHidden: [], crossHub: [] };

    const defaultsHidden = (sourceHub.items || []).filter((it) => !visibleKeySet.has(it.key));

    const defaultKeys = new Set(defaultsHidden.map((it) => it.key));
    const crossHub = [];
    const seen = new Set(defaultKeys);
    for (const other of allBaseHubs || []) {
      if (!other || other.id === sourceHub.id) continue;
      for (const it of other.items || []) {
        if (!it?.key || visibleKeySet.has(it.key) || seen.has(it.key)) continue;
        seen.add(it.key);
        crossHub.push({
          ...it,
          sourceHubId: other.id,
          sourceHubLabel: other.label,
        });
      }
    }
    return { defaultsHidden, crossHub };
  }, [baseHub, hub, allBaseHubs, visibleKeySet]);

  const addOptionsCount =
    addCandidates.defaultsHidden.length + addCandidates.crossHub.length;

  if (!open || !hub) return null;

  const syncMenuFavorite = async (key, isFavorite) => {
    try {
      if (isFavorite) await api.users.addMenuFavorite(key);
      else await api.users.removeMenuFavorite(key);
      emitMenuFavoritesChanged({ menuKey: key, isFavorite });
    } catch {
      /* best-effort sync with FavoritePagesTabs / menu-guide */
    }
  };

  const buildOverridePayload = (partial) => {
    const next = {
      ...(hubOverride || {}),
      ...partial,
    };
    const qp = pickQuickPicksForSave(
      Object.prototype.hasOwnProperty.call(partial, 'quickPicks')
        ? partial
        : hubOverride
    );
    if (qp !== undefined) next.quickPicks = qp;
    else delete next.quickPicks;
    return next;
  };

  const moveItem = (key, dir) => {
    const keys = visibleItems.map((i) => i.key);
    const idx = keys.indexOf(key);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= keys.length) return;
    const tmp = keys[idx];
    keys[idx] = keys[next];
    keys[next] = tmp;
    onSaveHubOverride?.(
      buildOverridePayload({
        itemOrder: keys,
        hiddenItemKeys: [...hiddenKeys],
      })
    );
  };

  const hideItem = (key) => {
    const nextHidden = new Set(hiddenKeys);
    nextHidden.add(key);
    const extras = Array.isArray(hubOverride?.extraItems)
      ? hubOverride.extraItems.filter((e) => e?.key !== key)
      : hubOverride?.extraItems;
    const qp = Array.isArray(hubOverride?.quickPicks)
      ? hubOverride.quickPicks.filter((k) => k !== key)
      : undefined;
    const payload = buildOverridePayload({
      itemOrder: visibleItems.map((i) => i.key).filter((k) => k !== key),
      hiddenItemKeys: [...nextHidden],
      ...(extras !== undefined ? { extraItems: extras } : {}),
      ...(qp !== undefined ? { quickPicks: qp } : {}),
    });
    onSaveHubOverride?.(payload);
  };

  const addItem = (it) => {
    if (!it?.key) return;
    const key = it.key;
    const nextHidden = new Set(hiddenKeys);
    nextHidden.delete(key);
    const order = visibleItems.map((i) => i.key);
    if (!order.includes(key)) order.push(key);

    const sourceHub = baseHub || hub;
    const inBase = (sourceHub?.items || []).some((i) => i.key === key);
    let extraItems = Array.isArray(hubOverride?.extraItems)
      ? [...hubOverride.extraItems]
      : [];

    if (!inBase) {
      if (!extraItems.some((e) => e?.key === key)) {
        extraItems.push({
          key: it.key,
          href: it.href,
          label: it.label,
          iconKey: it.iconKey,
          external: Boolean(it.external),
          disabled: Boolean(it.disabled),
          show: true,
        });
      }
    }

    onSaveHubOverride?.(
      buildOverridePayload({
        itemOrder: order,
        hiddenItemKeys: [...nextHidden],
        extraItems,
      })
    );
  };

  const toggleQuickPick = (key) => {
    const isFavorite = favoriteSet.has(key) || favoriteSet.has(
      visibleItems.find((i) => i.key === key)?.href
    );
    const current = Array.isArray(hubOverride?.quickPicks)
      ? [...hubOverride.quickPicks]
      : [];
    let nextFavorite;
    if (isFavorite) {
      nextFavorite = false;
      const idx = current.indexOf(key);
      if (idx >= 0) current.splice(idx, 1);
    } else {
      nextFavorite = true;
      if (!current.includes(key)) current.push(key);
    }
    onSaveHubOverride?.(
      buildOverridePayload({
        itemOrder: hubOverride?.itemOrder || visibleItems.map((i) => i.key),
        hiddenItemKeys: [...hiddenKeys],
        quickPicks: current,
      })
    );
    void syncMenuFavorite(key, nextFavorite);
  };

  const renderLink = (it) => {
    const ItemIcon = icons[it.iconKey] || icons.appstore;
    const isActive = selectedKey === it.key || selectedKey === it.href;
    const className = `mb-flyout-link ${isActive ? 'mb-flyout-link--active' : ''}`;
    const starred = Boolean(it.isFavorite) || favoriteSet.has(it.key) || favoriteSet.has(it.href);
    const body = (
      <>
        <span className="mb-flyout-link__icon">{ItemIcon ? <ItemIcon size={16} /> : null}</span>
        <span className="mb-flyout-link__label-row">
          <span className="mb-flyout-link__label">{it.label}</span>
          <NavBadge type={it.badge} />
          <NavFavoriteStar active={starred} />
        </span>
      </>
    );

    if (it.external) {
      return (
        <a
          key={it.key}
          href={it.href}
          className={className}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onNavigate?.()}
        >
          {body}
        </a>
      );
    }

    return (
      <Link
        key={it.key}
        to={it.href}
        className={className}
        onClick={() => onNavigate?.()}
      >
        {body}
      </Link>
    );
  };

  const handleAddSelect = (e) => {
    const key = e.target.value;
    e.target.value = '';
    if (!key) return;
    const fromDefaults = addCandidates.defaultsHidden.find((it) => it.key === key);
    const fromCross = addCandidates.crossHub.find((it) => it.key === key);
    addItem(fromDefaults || fromCross);
  };

  return (
    <>
      <button type="button" className="mb-flyout-backdrop" aria-label="Закрыть меню" onClick={onClose} />
      <div
        className={`mb-flyout ${editMode ? 'mb-flyout--edit' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mb-flyout-heading"
      >
        <div className="mb-flyout-header">
          <div className="mb-flyout-header-main">
            <span className="mb-flyout-header__icon">{Icon ? <Icon size={22} /> : null}</span>
            <div>
              <p className="mb-flyout-kicker">Навигация</p>
              <h2 className="mb-flyout-title" id="mb-flyout-heading">{hub.title || hub.label}</h2>
              {hub.sub ? <p className="mb-flyout-sub">{hub.sub}</p> : null}
            </div>
          </div>
          <div className="mb-flyout-header-actions">
            <button
              type="button"
              className={`mb-flyout-icon-btn ${editMode ? 'is-active' : ''}`}
              onClick={() => setEditMode((v) => !v)}
              title="Настроить раздел"
              aria-label="Настроить раздел"
            >
              <SettingOutlined size={18} />
            </button>
            <button type="button" className="mb-flyout-icon-btn" onClick={onClose} aria-label="Закрыть">
              <CloseOutlined size={18} />
            </button>
          </div>
        </div>

        <div className="mb-flyout-body">
          <div className="mb-flyout-cols">
            <div className="mb-flyout-col">
              <p className="mb-flyout-col__title">{editMode ? 'Пункты раздела' : 'Разделы'}</p>
              {!editMode && (
                <div className="mb-flyout-links">
                  {visibleItems.map(renderLink)}
                  {visibleItems.length === 0 && (
                    <p className="mb-flyout-empty">Нет доступных пунктов</p>
                  )}
                </div>
              )}
              {editMode && (
                <>
                  <ul className="mb-flyout-edit-list">
                    {visibleItems.map((it) => {
                      const starred = Boolean(it.isFavorite) || favoriteSet.has(it.key) || favoriteSet.has(it.href);
                      return (
                        <li key={it.key} className="mb-flyout-edit-row">
                          <span className="mb-flyout-edit-row__label">
                            {it.label}
                            {' '}
                            <NavBadge type={it.badge} />
                            <NavFavoriteStar active={starred} />
                          </span>
                          <span className="mb-flyout-edit-row__actions">
                            <button
                              type="button"
                              className={starred ? 'is-active' : ''}
                              title="Избранное (синхронизируется с верхним меню)"
                              onClick={() => toggleQuickPick(it.key)}
                            >
                              <Star size={14} strokeWidth={1.75} aria-hidden />
                            </button>
                            <button type="button" onClick={() => moveItem(it.key, -1)} aria-label="Выше">↑</button>
                            <button type="button" onClick={() => moveItem(it.key, 1)} aria-label="Ниже">↓</button>
                            <button type="button" onClick={() => hideItem(it.key)} aria-label="Убрать">✕</button>
                          </span>
                        </li>
                      );
                    })}
                    {visibleItems.length === 0 && (
                      <p className="mb-flyout-empty">Нет пунктов — добавьте из списка ниже</p>
                    )}
                  </ul>

                  <div className="mb-flyout-add-dropdown">
                    <label className="mb-flyout-col__title mb-flyout-add-dropdown__label" htmlFor="mb-flyout-add-select">
                      <PlusOutlined size={14} />
                      {' '}
                      Добавить пункт
                    </label>
                    <select
                      id="mb-flyout-add-select"
                      className="mb-flyout-add-select"
                      defaultValue=""
                      disabled={addOptionsCount === 0}
                      onChange={handleAddSelect}
                      aria-label="Добавить пункт в раздел"
                    >
                      <option value="">
                        {addOptionsCount === 0
                          ? 'Все доступные пункты уже в меню'
                          : 'Выберите пункт…'}
                      </option>
                      {addCandidates.defaultsHidden.length > 0 && (
                        <optgroup label="Скрытые в этом разделе">
                          {addCandidates.defaultsHidden.map((it) => (
                            <option key={it.key} value={it.key}>
                              {it.label}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {addCandidates.crossHub.length > 0 && (
                        <optgroup label="Из других разделов">
                          {addCandidates.crossHub.map((it) => (
                            <option key={`${it.sourceHubId}:${it.key}`} value={it.key}>
                              {it.sourceHubLabel}
                              :
                              {' '}
                              {it.label}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    <button
                      type="button"
                      className="mb-flyout-reset-btn"
                      onClick={() => onResetHub?.(hub.id)}
                    >
                      Сбросить раздел
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavFlyout;
